/**
 * DarkModeService - Theme preference management
 *
 * Supports the two dark mode triggers defined in
 * specs_and_project_plan/ionic-styling-architecture.md Section 2.6:
 * 1. System preference (`prefers-color-scheme: dark`) when mode is 'auto'
 * 2. Manual override via the `body.dark` class ('light' / 'dark' modes)
 *
 * The preference persists to localStorage under `checkerfly-theme-preference`
 * so the theme is restored on app restart. The service is theme-only state -
 * no PII, no Firestore.
 *
 * @see WI-052 - Settings View
 * @see specs_and_project_plan/dark-mode-implementation-guide.md
 */

import { Injectable, signal, Signal } from '@angular/core';
import { devLog } from '@core';
import { ThemeMode, THEME_STORAGE_KEY } from '../models/settings.models';

@Injectable({
  providedIn: 'root',
})
export class DarkModeService {
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  private readonly themeModeSignal = signal<ThemeMode>(this.readStoredMode());
  private readonly isDarkModeSignal = signal<boolean>(false);

  /** Current theme mode preference ('auto' | 'light' | 'dark'). */
  readonly themeMode: Signal<ThemeMode> = this.themeModeSignal.asReadonly();

  /** Whether dark mode is currently applied (resolves 'auto' via system). */
  readonly isDarkMode: Signal<boolean> = this.isDarkModeSignal.asReadonly();

  constructor() {
    // Re-evaluate when the system preference changes (affects 'auto' mode)
    this.mediaQuery.addEventListener('change', () => {
      if (this.themeModeSignal() === 'auto') {
        this.applyTheme();
      }
    });

    // Apply persisted preference on startup
    this.applyTheme();
  }

  /**
   * Set the theme mode and persist it to localStorage.
   *
   * @param mode - 'auto' (follow system), 'light', or 'dark'
   */
  setMode(mode: ThemeMode): void {
    this.themeModeSignal.set(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Storage unavailable (private mode / quota) - theme still applies for session
      devLog('[DarkModeService] Could not persist theme preference');
    }
    this.applyTheme();
    devLog('[DarkModeService] Theme mode set', { mode });
  }

  /**
   * Toggle between explicit light and dark modes.
   * Leaves 'auto' by design: toggling is an explicit user override.
   */
  toggle(): void {
    this.setMode(this.isDarkModeSignal() ? 'light' : 'dark');
  }

  /**
   * Read the persisted theme mode, defaulting to 'auto'.
   */
  private readStoredMode(): ThemeMode {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'auto') {
        return stored;
      }
    } catch {
      // Storage unavailable - fall through to default
    }
    return 'auto';
  }

  /**
   * Resolve the effective dark state and sync the `body.dark` class.
   */
  private applyTheme(): void {
    const dark = this.calculateIsDark();
    this.isDarkModeSignal.set(dark);
    document.body.classList.toggle('dark', dark);
  }

  /**
   * Compute whether dark mode should be active for the current mode.
   */
  private calculateIsDark(): boolean {
    const mode = this.themeModeSignal();
    if (mode === 'dark') {
      return true;
    }
    if (mode === 'light') {
      return false;
    }
    return this.mediaQuery.matches;
  }
}
